import {useEffect, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
	combinesDocumentPhotosWithDetails,
	REGISTRATION_EDIT_PHOTOS_PARAM,
	REGISTRATION_EDIT_PHOTOS_VALUE,
	RegistrationStep,
	usesForeignDocumentDetailsForm,
} from 'features/registration';
import type {RegistrationWizardState} from './useRegistrationWizardState';

/**
 * Вход с профиля после rejected: `/registration?edit=photos` → экран фото документов.
 * Фото у всех на DocumentDetails, как у РФ.
 * Без снимка сверки: после сохранения — verifyIdentity и возврат в профиль.
 * Если бэк прислал identityVerificationRequired: false — в профиль без вызова идентификации.
 */
export const useRegistrationPhotosEditEntry = (state: RegistrationWizardState) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const appliedRef = useRef(false);
	const {personalInfo, setReturnToIdentityReview, setStep} = state;

	const wantsPhotosEdit =
		searchParams.get(REGISTRATION_EDIT_PHOTOS_PARAM) === REGISTRATION_EDIT_PHOTOS_VALUE;

	useEffect(() => {
		if (appliedRef.current || !wantsPhotosEdit) {
			return;
		}

		if (!personalInfo.citizenshipOksmCode) {
			return;
		}

		appliedRef.current = true;

		const photosOnDetails = combinesDocumentPhotosWithDetails(personalInfo.citizenshipOksmCode);
		const usesForeign = usesForeignDocumentDetailsForm(
			personalInfo.citizenshipOksmCode,
			personalInfo.residenceOksmCode,
		);

		setReturnToIdentityReview(true);
		setStep(
			usesForeign && !photosOnDetails
				? RegistrationStep.DocumentPhotos
				: RegistrationStep.DocumentDetails,
		);
		setSearchParams(
			(current) => {
				const next = new URLSearchParams(current);
				next.delete(REGISTRATION_EDIT_PHOTOS_PARAM);
				return next;
			},
			{replace: true},
		);
	}, [
		personalInfo.citizenshipOksmCode,
		personalInfo.residenceOksmCode,
		setReturnToIdentityReview,
		setSearchParams,
		setStep,
		wantsPhotosEdit,
	]);
};
